import React, { useCallback, useEffect, useState } from "react";
import { Button, Drawer, Empty, Input, Select, Space, Table, Tag, Typography } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import ExclusionReasons from "./ExclusionReasons";
import HeaderHelp, { AIRING_COLUMN_HELP, SCORE_COLUMN_HELP, ScopeHelp, SeriesHelp } from "./HeaderHelp";
import {
  EpisodeAiringRow,
  ScoreFilterOptions,
  ScoreFilters,
  ScoreRow,
  downloadScoresCsv,
  fetchEpisodeAirings,
  fetchScoreFilterOptions,
  fetchScores,
} from "./api";

const DAY_NAME = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const pct = (v: number) => (v >= 0 ? "+" : "") + Number(v).toFixed(1) + "%";

const { Text } = Typography;
const BUCKET_COLOR: Record<string, string> = { high: "green", average: "default", low: "volcano" };

/** The hand-over table EPISODE_SCORES with filters. Defaults to the usable view: feed scope, strong confidence. */
const ScoresTab: React.FC<{ refreshKey: number }> = ({ refreshKey }) => {
  const [options, setOptions] = useState<ScoreFilterOptions | null>(null);
  const [filters, setFilters] = useState<ScoreFilters>({ scope: "feed", confidence: "strong" });
  const [rows, setRows] = useState<ScoreRow[]>([]);
  const [total, setTotal] = useState(0);
  const [hiddenByConfidence, setHiddenByConfidence] = useState(0);
  const [page, setPage] = useState({ current: 1, pageSize: 50 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<ScoreRow | null>(null);
  const [airings, setAirings] = useState<EpisodeAiringRow[]>([]);
  const [airingsLoading, setAiringsLoading] = useState(false);

  const openAirings = (row: ScoreRow) => {
    setSelected(row);
    setAiringsLoading(true);
    fetchEpisodeAirings(row.REF_ID, { feed: row.SCOPE === "overall" ? null : row.FEED_CODE, platform: row.PLATFORM })
      .then((data) => setAirings(data.rows))
      .finally(() => setAiringsLoading(false));
  };

  useEffect(() => {
    fetchScoreFilterOptions().then((o) => {
      setOptions(o);
      setFilters((f) => (f.feed || !o.feeds.length ? f : { ...f, feed: o.feeds.includes("NINTL1") ? "NINTL1" : o.feeds[0] }));
    });
  }, [refreshKey]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchScores(filters, page.current, page.pageSize);
      setRows(data.rows);
      setTotal(data.total);
      setHiddenByConfidence(Math.max(0, (data.totalAllConfidence ?? data.total) - data.total));
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const update = (patch: Partial<ScoreFilters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage((p) => ({ ...p, current: 1 }));
  };

  return (
    <div style={{ padding: 24 }}>
      <Space wrap style={{ marginBottom: 16 }}>
        <ScopeHelp />
        <Select
          value={filters.scope}
          style={{ width: 130 }}
          onChange={(scope) => update({ scope, feed: scope === "overall" ? undefined : filters.feed, platform: undefined })}
          options={[
            { value: "feed", label: "Per feed" },
            { value: "platform", label: "Per platform" },
            { value: "overall", label: "Overall" },
          ]}
        />
        {filters.scope !== "overall" && (
          <Select
            allowClear
            placeholder="Feed"
            value={filters.feed}
            style={{ width: 150 }}
            onChange={(feed) => update({ feed })}
            options={(options?.feeds || []).map((f) => ({ value: f, label: f }))}
          />
        )}
        {filters.scope === "platform" && (
          <Select
            allowClear
            placeholder="Platform"
            value={filters.platform}
            style={{ width: 230 }}
            onChange={(platform) => update({ platform })}
            options={(options?.platforms || []).map((p) => ({ value: p, label: p }))}
          />
        )}
        <SeriesHelp />
        <Select
          allowClear
          placeholder="Series"
          value={filters.series}
          style={{ width: 200 }}
          onChange={(series) => update({ series })}
          options={(options?.series || []).map((s) => ({ value: s, label: s }))}
        />
        <Select
          allowClear
          placeholder="Confidence"
          value={filters.confidence}
          style={{ width: 140 }}
          onChange={(confidence) => update({ confidence })}
          options={[
            { value: "strong", label: "Strong only" },
            { value: "thin", label: "Thin only" },
          ]}
        />
        <Input.Search placeholder="Title or ref id" allowClear style={{ width: 240 }} onSearch={(q) => update({ q: q || undefined })} />
        <Button icon={<DownloadOutlined />} onClick={() => downloadScoresCsv(filters)}>
          Download CSV
        </Button>
        {options?.asOfDate && (
          <Text type="secondary">
            Data through {options.asOfDate}
            {options.computedAt ? `, computed ${new Date(options.computedAt).toLocaleString()}` : ""}
          </Text>
        )}
      </Space>

      <Table
        size="middle"
        rowKey={(r) => `${r.REF_ID}|${r.SCOPE}|${r.FEED_CODE}|${r.PLATFORM}`}
        loading={loading}
        dataSource={rows}
        onRow={(row) => ({ onClick: () => openAirings(row), style: { cursor: "pointer" } })}
        pagination={{
          current: page.current,
          pageSize: page.pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: [25, 50, 100, 200],
          onChange: (current, pageSize) => setPage({ current, pageSize }),
          onShowSizeChange: (_current, pageSize) => setPage({ current: 1, pageSize }),
        }}
        scroll={{ x: filters.scope === "platform" ? 1300 : 1100 }}
        locale={{
          emptyText:
            !loading && total === 0 && hiddenByConfidence > 0 ? (
              <Empty
                description={
                  <>
                    No {filters.confidence} scores match, but {hiddenByConfidence.toLocaleString()} {filters.confidence === "strong" ? "thin" : "strong"}{" "}
                    {hiddenByConfidence === 1 ? "one does" : "ones do"}.{" "}
                    <Button type="link" style={{ padding: 0 }} onClick={() => update({ confidence: undefined })}>
                      Show all confidence levels
                    </Button>
                  </>
                }
              />
            ) : (
              <Empty description={filters.series || filters.q ? "No scores match these filters" : "No scores yet. Upload a report on the Upload tab."} />
            ),
        }}
        columns={[
          { title: "Series", dataIndex: "SERIES", width: 170, ellipsis: true },
          { title: "Title", dataIndex: "TITLE", ellipsis: true },
          { title: "Ref ID", dataIndex: "REF_ID", width: 110 },
          ...(filters.scope === "platform" ? [{ title: "Platform", dataIndex: "PLATFORM", width: 200, ellipsis: true }] : []),
          { title: <HeaderHelp label="Score %" help={SCORE_COLUMN_HELP.score} />, dataIndex: "SCORE_PCT", width: 120, align: "right" as const, render: (v: number) => (v >= 0 ? "+" : "") + Number(v).toFixed(1) },
          { title: <HeaderHelp label="Bucket" help={SCORE_COLUMN_HELP.bucket} />, dataIndex: "BUCKET", width: 120, align: "center" as const, render: (v: string) => <Tag color={BUCKET_COLOR[v] || "default"}>{v}</Tag> },
          { title: "Airings", dataIndex: "AIRINGS", width: 110, align: "center" as const },
          { title: <HeaderHelp label="Confidence" help={SCORE_COLUMN_HELP.confidence} />, dataIndex: "CONFIDENCE", width: 140, align: "center" as const },
          { title: <HeaderHelp label="Source" help={SCORE_COLUMN_HELP.source} />, dataIndex: "SOURCE", width: 120, align: "center" as const, render: (v: string) => (v === "borrowed" ? <Tag>borrowed</Tag> : v) },
        ]}
      />

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        width={1280}
        title={selected ? `${selected.SERIES}: ${selected.TITLE} (${selected.REF_ID})` : ""}
      >
        {selected && (
          <>
            <Space style={{ marginBottom: 12 }} wrap>
              <Text>
                Score <Text strong>{pct(selected.SCORE_PCT)}</Text>
              </Text>
              {airings.length > 0 && (
                <Text type="secondary">
                  (plain average of the {airings.length} airings below:{" "}
                  {pct(airings.reduce((sum, a) => sum + a.airingPct, 0) / airings.length)})
                </Text>
              )}
              <Tag color={BUCKET_COLOR[selected.BUCKET] || "default"}>{selected.BUCKET}</Tag>
              <Text type="secondary">
                {selected.SCOPE === "platform" ? selected.PLATFORM : selected.SCOPE === "feed" ? `feed ${selected.FEED_CODE}` : "overall"},{" "}
                {selected.AIRINGS} airings, {selected.CONFIDENCE}
              </Text>
            </Space>
            <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
              Each airing against the slot it landed in: what an average episode of this series earns there
              (expected), what this airing earned (rate), and the difference. The score starts as the plain
              average of the &quot;Airing vs slot&quot; column, is pulled toward 0 when there are few airings, and for
              a feed is blended across its platforms by minutes watched. Airings the cleaning step excluded are not
              listed. <ExclusionReasons />
            </Text>
            <Table
              size="small"
              rowKey={(r) => `${r.platform}|${r.channel}|${r.startLocal}`}
              loading={airingsLoading}
              dataSource={airings}
              pagination={{ defaultPageSize: 25, showSizeChanger: true, pageSizeOptions: [25, 50, 100, 200] }}
              scroll={{ x: 1220 }}
              columns={[
                { title: "Aired (local)", dataIndex: "startLocal", width: 150, render: (v: string) => v?.replace("T", " ").slice(0, 16) },
                { title: "Slot", key: "slot", width: 90, render: (_: unknown, r: EpisodeAiringRow) => `${DAY_NAME[r.dayOfWeek]} ${String(r.hourOfDay).padStart(2, "0")}:00` },
                { title: "Platform", dataIndex: "platform", width: 190, ellipsis: true },
                { title: "Channel", dataIndex: "channel", width: 140, ellipsis: true },
                { title: <HeaderHelp label="Slot vs average" help={AIRING_COLUMN_HELP.slotVsAverage} />, dataIndex: "baselineIndex", width: 140, align: "right", render: (v: number) => pct((v - 1) * 100) },
                { title: <HeaderHelp label="Expected rate" help={AIRING_COLUMN_HELP.expectedRate} />, dataIndex: "expectedRate", width: 130, align: "right", render: (v: number) => v.toFixed(2) },
                { title: <HeaderHelp label="Actual rate" help={AIRING_COLUMN_HELP.actualRate} />, dataIndex: "rate", width: 120, align: "right", render: (v: number) => v.toFixed(2) },
                { title: <HeaderHelp label="Airing vs slot" help={AIRING_COLUMN_HELP.airingVsSlot} />, dataIndex: "airingPct", width: 130, align: "right", render: (v: number) => <Text type={v >= 0 ? "success" : "danger"}>{pct(v)}</Text>, sorter: (a, b) => a.airingPct - b.airingPct },
                { title: "Minutes watched", dataIndex: "viewershipMin", width: 100, align: "right", render: (v: number) => Math.round(v).toLocaleString() },
                { title: <HeaderHelp label="Merged pieces" help={AIRING_COLUMN_HELP.mergedPieces} />, dataIndex: "mergedRows", width: 130, align: "right", render: (v: number) => (v > 1 ? v : "") },
              ]}
            />
          </>
        )}
      </Drawer>
    </div>
  );
};

export default ScoresTab;
